import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Wallet, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, TableShell, TR, TD } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const TXN_LABEL = { sale_pending: "Sale (clearing)", sale_available: "Sale (available)", withdrawal: "Withdrawal", refund: "Refund", adjust: "Adjustment" };

export default function FreelancerWallet() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ upi_id: "", account_name: "", account_number: "", ifsc: "" });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/mkt/freelancer/wallet").then((r) => {
    setData(r.data);
    const pd = r.data.payout_details || {};
    setPayForm({ upi_id: pd.upi_id || "", account_name: pd.account_name || "", account_number: pd.account_number || "", ifsc: pd.ifsc || "" });
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <LoadingRows rows={4} />;
  const w = data.wallet;
  const hasPayout = (data.payout_details || {}).account_number || (data.payout_details || {}).upi_id;

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/mkt/freelancer/withdrawals", { amount: Number(amount) });
      toast.success("Withdrawal requested — admin will process it"); setOpen(false); setAmount(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="freelancer-wallet-page">
      <PageHeader title="Wallet & Payouts" subtitle="Marketplace earnings are separate from website/CRM income" testid="wallet-title">
        <Button data-testid="withdraw-btn" onClick={() => setOpen(true)} disabled={!w.available || w.available < data.min_withdrawal}
          className="rounded-full font-bold gradient-bg text-white border-0 glow-sm">
          <ArrowDownToLine className="h-4 w-4 mr-1.5" />Withdraw
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Total Earnings", w.total_earnings, "linear-gradient(135deg,#007A4D,#059669)"],
          ["Pending Clearance", w.pending, "linear-gradient(135deg,#d97706,#f59e0b)"],
          ["Available", w.available, "linear-gradient(135deg,#2563EB,#06B6D4)"],
          ["Withdrawn", w.withdrawn, "linear-gradient(135deg,#7C3AED,#DB2777)"],
        ].map(([label, val, bg], i) => (
          <div key={label} data-testid={`wallet-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl p-5 text-white" style={{ background: bg }}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/75">{label}</p>
            <p className="font-display font-extrabold text-2xl mt-1.5">{fmtINR(val || 0)}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5" data-testid="payout-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display font-bold text-sm">Payout Details (Bank / UPI)</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasPayout
                ? `${data.payout_details.upi_id ? `UPI: ${data.payout_details.upi_id}` : ""}${data.payout_details.account_number ? ` A/C: ${data.payout_details.account_number}` : ""}`
                : "Add payout details to enable withdrawals"}
            </p>
          </div>
          <Button variant="outline" data-testid="payout-edit-btn" onClick={() => setPayOpen(true)} className="rounded-full font-bold">
            {hasPayout ? "Edit" : "Add Payout Details"}
          </Button>
        </div>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Wallet className="h-5 w-5" /> Transactions</h2>
        {data.transactions.length === 0 ? <p className="text-sm text-muted-foreground">No transactions yet.</p> : (
          <TableShell head={["Type", "Amount", "Date"]} testid="wallet-transactions">
            {data.transactions.map((t) => (
              <TR key={t.id} testid={`txn-${t.id}`}>
                <TD><span className="font-semibold text-sm">{TXN_LABEL[t.type] || t.type}</span></TD>
                <TD><span className={`font-bold ${t.type === "withdrawal" || t.type === "refund" ? "text-red-500" : "text-emerald-600 dark:text-emerald-400"}`}>
                  {t.type === "withdrawal" || t.type === "refund" ? "−" : "+"}{fmtINR(t.amount)}</span></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Withdrawal Requests</h2>
        {data.withdrawals.length === 0 ? <p className="text-sm text-muted-foreground">No withdrawals yet. Minimum withdrawal: {fmtINR(data.min_withdrawal)}.</p> : (
          <TableShell head={["Amount", "Status", "Reference", "Requested"]} testid="wallet-withdrawals">
            {data.withdrawals.map((r) => (
              <TR key={r.id} testid={`withdrawal-${r.id}`}>
                <TD><span className="font-bold">{fmtINR(r.amount)}</span></TD>
                <TD><StatusBadge status={r.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{r.reference || "—"}</span></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payout-dialog">
          <DialogHeader><DialogTitle className="font-display">Payout Details</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>UPI ID</Label><Input data-testid="payout-upi" value={payForm.upi_id} onChange={(e) => setPayForm({ ...payForm, upi_id: e.target.value })} placeholder="name@upi" /></div>
            <div className="space-y-1.5"><Label>Account Holder Name</Label><Input data-testid="payout-acc-name" value={payForm.account_name} onChange={(e) => setPayForm({ ...payForm, account_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Account Number</Label><Input data-testid="payout-acc-number" value={payForm.account_number} onChange={(e) => setPayForm({ ...payForm, account_number: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>IFSC</Label><Input data-testid="payout-ifsc" value={payForm.ifsc} onChange={(e) => setPayForm({ ...payForm, ifsc: e.target.value })} /></div>
            </div>
            <Button data-testid="payout-save" disabled={busy} onClick={async () => {
              setBusy(true);
              try { await api.post("/mkt/freelancer/payout", payForm); toast.success("Payout details saved"); setPayOpen(false); load(); }
              catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
            }} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Payout Details"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle className="font-display">Request Withdrawal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Available balance: <b>{fmtINR(w.available)}</b> · Minimum {fmtINR(data.min_withdrawal)}</p>
            <div className="space-y-1.5">
              <Label>Amount (₹)</Label>
              <Input data-testid="withdraw-amount" type="number" min={data.min_withdrawal} max={w.available} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <Button data-testid="withdraw-submit" onClick={withdraw} disabled={busy || !amount} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Withdraw ${amount ? fmtINR(amount) : ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
